/**
 * Glass bubbles drifting up the title screen behind the logo and the dragons, each wobbling as it
 * rises, leaving at the top and coming back in at the bottom. Their places are a pure function of
 * time, so every frame (and every capture) at a given `t` is the same. They keep to lanes either
 * side of the menu band so they never pass behind the menu's text.
 */

import { Group, type Material, Mesh, SphereGeometry } from 'three';
import { lcg } from './backdrop';

/** World y a bubble leaves at the top and re-enters at the bottom (off screen both ways). */
const TOP = 15.5;
const BOTTOM = -15.5;

interface Drift {
  mesh: Mesh;
  x: number;
  z: number;
  r: number;
  speed: number;
  start: number;
  wobble: number;
}

export class RisingBubbles {
  readonly group = new Group();
  private readonly sphere = new SphereGeometry(1, 40, 28);
  private readonly drifts: Drift[] = [];

  /** `count` bubbles in the given glass materials, in lanes `minX`..`maxX` tiles either side of centre. */
  constructor(glass: readonly Material[], count: number, minX: number, maxX: number, seed = 0x5eed) {
    const rand = lcg(seed);
    for (let i = 0; i < count; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      const r = 0.45 + rand() * 0.6;
      const drift: Drift = {
        mesh: new Mesh(this.sphere, glass[i % glass.length]),
        x: side * (minX + r + rand() * (maxX - minX - 2 * r)),
        // Behind the logo and the figures, in front of the wall.
        z: -(r + 0.15) - rand() * 0.3,
        r,
        speed: 1.1 + rand() * 1.3,
        start: rand() * (TOP - BOTTOM),
        wobble: rand() * Math.PI * 2,
      };
      drift.mesh.scale.setScalar(r);
      this.drifts.push(drift);
      this.group.add(drift.mesh);
    }
  }

  /** Place every bubble at `t` seconds. */
  update(t: number): void {
    const span = TOP - BOTTOM;
    for (const d of this.drifts) {
      const run = (d.start + d.speed * t) % span;
      const y = BOTTOM + run;
      const sway = Math.sin(t * 1.3 + d.wobble) * 0.35;
      d.mesh.position.set(d.x + sway, y, d.z);
      // A blown bubble is never quite round: it breathes a little as it goes.
      const breathe = Math.sin(t * 3.1 + d.wobble * 2) * 0.03;
      d.mesh.scale.set(d.r * (1 + breathe), d.r * (1 - breathe), d.r);
    }
  }

  dispose(): void {
    this.sphere.dispose();
  }
}
